/**
 * AppLayout — Dual-mode layout wrapper
 *
 * In EMBEDDED mode (AngularJS host): Renders children only — AngularJS owns the chrome.
 * In STANDALONE mode (React host): Renders full layout shell (header, sidebar, subnav, footer).
 */

import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getMountConfig } from '@/mountConfig';
import { useUIStore } from '@/store/uiStore';
import { useGlobalHotkeys } from '@/hooks/useGlobalHotkeys';
import { useRouteDocumentTitle } from '@/hooks/useRouteDocumentTitle';
import { GlobalModalHost } from '@/features/globalModals/GlobalModalHost';
import AppHeader from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { AppSubNav } from './AppSubNav';
import { AppFooter } from './AppFooter';
import { StandaloneSpinner } from './StandaloneSpinner';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { mode } = getMountConfig();
  const location = useLocation();
  // Single selector: in embedded mode AngularJS owns the spinner, so short-circuit
  // to false and avoid store-driven rerenders (the layout returns children only).
  // Suppress the global spinner while a SafeView iframe route is mounted — SafeView
  // shows its own spinner inside the iframe, so ours would be a second one.
  const showSpinner = useUIStore(
    (s) => mode !== 'embedded' && s.isLoading && s.safeViewHostCount === 0
  );

  // Keyboard shortcuts (Ctrl+Alt+A/T, Ctrl+Alt+Z/H, Ctrl+Shift+Arrows)
  // Fires in both standalone and embedded modes — matches AngularJS Mousetrap bindings
  useGlobalHotkeys();

  // Browser tab title — mirrors AngularJS $routeChangeSuccess + index.html
  // <title>{{G_PAGE_TITLE}} - SafeView</title>. Fires for every React route
  // in both standalone and embedded modes so the tab stays accurate even
  // when React Router navigates without round-tripping AngularJS.
  useRouteDocumentTitle();

  // In embedded mode, AngularJS owns the layout — just render route content
  if (mode === 'embedded') {
    return <>{children}</>;
  }

  // Vendor routes are fullscreen — no staff chrome (header / sidebar / subnav /
  // footer / global modals). Matches the AngularJS `body.vendor-connect-fullscreen`
  // class that hid the chrome via CSS, and avoids the header/sidebar firing
  // staff API calls (recent orders, pending counts, etc.) that 401 for vendor
  // users and trigger the global session-expired redirect.
  const isVendorRoute =
    location.pathname.startsWith('/vendor-connect') ||
    location.pathname.startsWith('/VendorConnect');
  if (isVendorRoute) {
    return <>{children}</>;
  }

  // VendorView (`/Location/vendorview`) uses the AngularJS `.fullscreenOverlay`
  // pattern: a `position: fixed; top: 50px; z-index: 999999` panel covering
  // everything except the top header. To match that look we render only the
  // header here — the sidebar / subnav / footer / global modals would otherwise
  // sit behind the overlay but still peek through gaps (the overlay starts at
  // top:50px, so the subnav at top:50px lines up directly under it; and the
  // sidebar starts at left:0 where the Options button sits at left:130px,
  // partially hiding it).
  //
  // Geomap (`/Location/geomap`) is NOT fullscreen — legacy renders it inside
  // the normal AngularJS shell so the global workorder search in the sidebar
  // stays reachable. Without the sidebar, the user has no way to load an
  // order and the route is stuck on "Please load order".
  const isFullscreenMapRoute = location.pathname.startsWith('/Location/vendorview');
  if (isFullscreenMapRoute) {
    return (
      <div className='app-layout min-h-screen'>
        <AppHeader />
        <main className='app-layout__main'>{children}</main>
        {showSpinner && <StandaloneSpinner />}
      </div>
    );
  }

  // In standalone mode, React owns the full layout.
  // Sidebar is position: fixed (matches AngularJS) — main gets margin-left from CSS.
  return (
    <div className='app-layout min-h-screen'>
      <AppHeader />
      <AppSubNav />
      <AppSidebar />
      <main className='app-layout__main'>{children}</main>
      <AppFooter />
      <GlobalModalHost />

      {/* Standalone loading overlay — matches AngularJS loadingWidget directive.
          Uses the user-selected spinner GIF from G_SETTINGS.loadingSpinner.
          Default: safeview.gif. Changed via footer Customize panel. */}
      {showSpinner && <StandaloneSpinner />}
    </div>
  );
}
